import React, { useState, useEffect } from 'react';
import api from '../utils/api';
import { AlertCircle, AlertTriangle, Info, Plus, Edit2, Trash2, Eye, EyeOff, Languages } from 'lucide-react';

const AdminInfoAlerts = () => {
  const [alerts, setAlerts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [translating, setTranslating] = useState(false);

  const [formData, setFormData] = useState({
    title_de: '',
    title_en: '',
    content_de: '',
    content_en: '',
    type: 'info',
    active: true
  });

  useEffect(() => {
    fetchAlerts();
  }, []);

  const fetchAlerts = async () => {
    try {
      setLoading(true);
      const response = await api.get('/info-alerts?active_only=false');
      setAlerts(response.data);
    } catch (err) {
      console.error('Error fetching info alerts:', err);
      setError('Fehler beim Laden der Info-Meldungen');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      title_de: '',
      title_en: '',
      content_de: '',
      content_en: '',
      type: 'info',
      active: true
    });
    setEditingId(null);
    setShowForm(false);
    setError('');
    setSuccess('');
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      if (editingId) {
        await api.put(`/info-alerts/${editingId}`, formData);
        setSuccess('Info-Meldung erfolgreich aktualisiert!');
      } else {
        await api.post('/info-alerts', formData);
        setSuccess('Info-Meldung erfolgreich erstellt!');
      }
      await fetchAlerts();
      resetForm();
    } catch (err) {
      setError(err.response?.data?.detail || 'Fehler beim Speichern der Info-Meldung');
    }
  };

  const handleEdit = (alert) => {
    setFormData({
      title_de: alert.title_de,
      title_en: alert.title_en,
      content_de: alert.content_de,
      content_en: alert.content_en,
      type: alert.type,
      active: alert.active
    });
    setEditingId(alert.id);
    setShowForm(true);
    setError('');
    setSuccess('');
  };

  const handleDelete = async (alertId) => {
    if (!window.confirm('Möchten Sie diese Info-Meldung wirklich löschen?')) {
      return;
    }

    try {
      await api.delete(`/info-alerts/${alertId}`);
      setSuccess('Info-Meldung erfolgreich gelöscht');
      await fetchAlerts();
    } catch (err) {
      setError('Fehler beim Löschen der Info-Meldung');
    }
  };

  const handleAutoTranslate = async () => {
    // Check if German fields are filled
    if (!formData.title_de && !formData.content_de) {
      alert('Bitte füllen Sie zuerst die deutschen Felder aus!');
      return;
    }

    setTranslating(true);
    try {
      const response = await api.post('/info-alerts/translate', {
        title_de: formData.title_de,
        content_de: formData.content_de
      });

      // Update form with translations
      setFormData({
        ...formData,
        title_en: response.data.title_en || formData.title_en,
        content_en: response.data.content_en || formData.content_en
      });

      setSuccess('✅ Übersetzung erfolgreich! Sie können die Texte jetzt anpassen.');
      setTimeout(() => setSuccess(''), 3000);
    } catch (error) {
      console.error('Translation error:', error);
      if (error.response?.status === 503) {
        setError('⚠️ DeepL API nicht konfiguriert. Bitte konfigurieren Sie den API-Key in den Einstellungen.');
      } else {
        setError('❌ Übersetzung fehlgeschlagen. Bitte versuchen Sie es erneut.');
      }
      setTimeout(() => setError(''), 5000);
    } finally {
      setTranslating(false);
    }
  };

  const toggleActive = async (alert) => {
    try {
      await api.put(`/info-alerts/${alert.id}`, { active: !alert.active });
      setSuccess(`Info-Meldung ${!alert.active ? 'aktiviert' : 'deaktiviert'}`);
      await fetchAlerts();
    } catch (err) {
      setError('Fehler beim Ändern des Status');
    }
  };

  const getTypeIcon = (type) => {
    switch (type) {
      case 'warning':
        return <AlertTriangle className="w-5 h-5" />;
      case 'important':
        return <AlertCircle className="w-5 h-5" />;
      default:
        return <Info className="w-5 h-5" />;
    }
  };

  const getTypeColor = (type) => {
    switch (type) {
      case 'warning':
        return 'bg-yellow-100 text-yellow-800 border-yellow-300';
      case 'important':
        return 'bg-red-100 text-red-800 border-red-300';
      default:
        return 'bg-blue-100 text-blue-800 border-blue-300';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Info-Meldungen</h1>
          <p className="mt-2 text-gray-600">Wichtige Informationen für die Startseite verwalten</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center space-x-2 bg-cyan-600 text-white px-4 py-2 rounded-lg hover:bg-cyan-700 transition-colors"
        >
          <Plus className="w-5 h-5" />
          <span>Neue Info-Meldung</span>
        </button>
      </div>

      {error && (
        <div className="p-4 bg-red-50 text-red-800 rounded-lg">
          {error}
        </div>
      )}

      {success && (
        <div className="p-4 bg-green-50 text-green-800 rounded-lg">
          {success}
        </div>
      )}

      {/* Form */}
      {showForm && (
        <div className="bg-white rounded-lg shadow-md p-6">
          <h2 className="text-xl font-semibold mb-4">
            {editingId ? 'Info-Meldung bearbeiten' : 'Neue Info-Meldung erstellen'}
          </h2>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">Typ</label>
              <select
                value={formData.type}
                onChange={(e) => setFormData({ ...formData, type: e.target.value })}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
              >
                <option value="info">ℹ️ Info (Blau)</option>
                <option value="warning">⚠️ Warnung (Gelb)</option>
                <option value="important">🔴 Wichtig (Rot)</option>
              </select>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titel (Deutsch)</label>
                <input
                  type="text"
                  value={formData.title_de}
                  onChange={(e) => setFormData({ ...formData, title_de: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Titel (English)</label>
                <input
                  type="text"
                  value={formData.title_en}
                  onChange={(e) => setFormData({ ...formData, title_en: e.target.value })}
                  required
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Auto-translate button */}
            <div className="flex justify-center py-2">
              <button
                type="button"
                onClick={handleAutoTranslate}
                disabled={translating}
                className="flex items-center space-x-2 bg-green-600 text-white px-6 py-2 rounded-lg hover:bg-green-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                <Languages className="w-5 h-5" />
                <span>{translating ? 'Übersetze...' : 'DE → EN Übersetzen'}</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Inhalt (Deutsch)
                  <span className="text-xs text-gray-500 ml-2">Enter für Zeilenumbruch</span>
                </label>
                <textarea
                  value={formData.content_de}
                  onChange={(e) => setFormData({ ...formData, content_de: e.target.value })}
                  required
                  rows={5}
                  placeholder="Geben Sie den Text ein. Drücken Sie Enter für Zeilenumbrüche."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Inhalt (English)
                  <span className="text-xs text-gray-500 ml-2">Enter for line break</span>
                </label>
                <textarea
                  value={formData.content_en}
                  onChange={(e) => setFormData({ ...formData, content_en: e.target.value })}
                  required
                  rows={5}
                  placeholder="Enter the text. Press Enter for line breaks."
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-cyan-500 focus:border-transparent"
                />
              </div>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="active"
                checked={formData.active}
                onChange={(e) => setFormData({ ...formData, active: e.target.checked })}
                className="w-4 h-4 text-cyan-600 border-gray-300 rounded focus:ring-cyan-500"
              />
              <label htmlFor="active" className="text-sm font-medium text-gray-700">
                Sofort aktiv schalten
              </label>
            </div>

            <div className="flex space-x-3">
              <button
                type="submit"
                className="bg-cyan-600 text-white px-6 py-2 rounded-lg hover:bg-cyan-700 transition-colors"
              >
                {editingId ? 'Aktualisieren' : 'Erstellen'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-300 text-gray-700 px-6 py-2 rounded-lg hover:bg-gray-400 transition-colors"
              >
                Abbrechen
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Alerts List */}
      <div className="bg-white rounded-lg shadow-md p-6">
        <h2 className="text-xl font-semibold mb-4">Aktuelle Info-Meldungen ({alerts.length})</h2>
        
        {loading ? (
          <p className="text-gray-600">Lade Info-Meldungen...</p>
        ) : alerts.length === 0 ? (
          <p className="text-gray-600">Noch keine Info-Meldungen vorhanden.</p>
        ) : (
          <div className="space-y-4">
            {alerts.map((alert) => (
              <div
                key={alert.id}
                className={`border-2 rounded-lg p-4 ${getTypeColor(alert.type)} ${
                  !alert.active ? 'opacity-50' : ''
                }`}
              >
                <div className="flex items-start justify-between">
                  <div className="flex items-start space-x-3 flex-1">
                    {getTypeIcon(alert.type)}
                    <div className="flex-1">
                      <div className="flex items-center space-x-2 mb-2">
                        <h3 className="font-semibold">{alert.title_de}</h3>
                        <span className="text-xs px-2 py-1 bg-white rounded">
                          {alert.type === 'info' ? 'Info' : alert.type === 'warning' ? 'Warnung' : 'Wichtig'}
                        </span>
                        {!alert.active && (
                          <span className="text-xs px-2 py-1 bg-gray-300 text-gray-700 rounded">
                            Inaktiv
                          </span>
                        )}
                      </div>
                      <p className="text-sm mb-1">{alert.content_de}</p>
                      <p className="text-xs opacity-75">EN: {alert.title_en} - {alert.content_en}</p>
                    </div>
                  </div>
                  
                  <div className="flex items-center space-x-2 ml-4">
                    <button
                      onClick={() => toggleActive(alert)}
                      className="p-2 hover:bg-white rounded transition-colors"
                      title={alert.active ? 'Deaktivieren' : 'Aktivieren'}
                    >
                      {alert.active ? <Eye className="w-4 h-4" /> : <EyeOff className="w-4 h-4" />}
                    </button>
                    <button
                      onClick={() => handleEdit(alert)}
                      className="p-2 hover:bg-white rounded transition-colors"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => handleDelete(alert.id)}
                      className="p-2 hover:bg-white rounded transition-colors"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminInfoAlerts;
